import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export const SERVICES = [
  {
    id: "custom-software",
    title: "Custom Software Development",
    summary:
      "Mission-critical platforms architected to outlast their first roadmap. We design for the version you don't have requirements for yet.",
    bullets: [
      "Domain-driven, modular architectures",
      "Production from day one—no proof-of-concept theatre",
      "Built for audit, scale, and graceful succession",
    ],
  },
  {
    id: "web-mobile",
    title: "Web & Mobile Applications",
    summary:
      "Customer-facing products that earn their daily-active users. Performance, accessibility, and storytelling treated as engineering disciplines.",
    bullets: [
      "React, Next.js, Swift, Kotlin, React Native",
      "Sub-second TTI on flagship surfaces",
      "WCAG 2.2 AA conformance as a default, not an upgrade",
    ],
  },
  {
    id: "cloud-engineering",
    title: "Cloud & DevSecOps",
    summary:
      "From greenfield landing zones to enterprise migrations. We bring policy-as-code, FinOps, and zero-trust as opinionated defaults.",
    bullets: [
      "AWS, Azure, GCP—two-cloud fluency",
      "Terraform-first, GitOps-native delivery",
      "FinOps practice that cuts spend without cutting velocity",
    ],
  },
  {
    id: "ai-ml",
    title: "AI & Data Engineering",
    summary:
      "Models that ship, get audited, and survive production. We build the unglamorous middle layer between your data and your decisions.",
    bullets: [
      "Feature stores, model registries, evaluation harnesses",
      "LLM systems with guardrails and graceful failure",
      "Governance compatible with regulated industries",
    ],
  },
  {
    id: "qa-testing",
    title: "Quality Engineering",
    summary:
      "Automation, performance, and accessibility testing baked into the pipeline. The kind of QA that catches the bug your customer would have caught first.",
    bullets: [
      "Playwright, Cypress, contract testing",
      "Performance budgets enforced in CI",
      "Accessibility audits aligned to WCAG 2.2",
    ],
  },
  {
    id: "ux-design",
    title: "Product & UX Design",
    summary:
      "Discovery, wireframes, and high-fidelity systems thinking. Design that makes complex enterprise products feel inevitable.",
    bullets: [
      "Workshop-led discovery with executive stakeholders",
      "Design systems engineered for component reuse",
      "Usability research with the people who actually click",
    ],
  },
  {
    id: "dedicated-teams",
    title: "Dedicated Engineering Teams",
    summary:
      "Embedded squads that operate inside your culture, on your tooling, on your roadmap. Time-zone-fluid, outcome-accountable, easy to scale.",
    bullets: [
      "From 3-person pods to 30-engineer programs",
      "Shared rituals, shared CI, shared accountability",
      "Replacement-rate guarantees on every engagement",
    ],
  },
  {
    id: "modernization",
    title: "Legacy Modernization",
    summary:
      "Migrate without melting. We strangle monoliths the boring, predictable way—incrementally, with an exit door at every checkpoint.",
    bullets: [
      "Risk-mapped modernization roadmaps",
      "Strangler-fig patterns and parallel-run validation",
      "Knowledge transfer that survives staff turnover",
    ],
  },
];

export const INDUSTRIES = [
  {
    id: "fintech",
    name: "FinTech",
    headline: "Capital-grade software for ledgers, lending, and trading.",
    description:
      "Trade finance, capital markets, lending, payments, and risk—built to satisfy the auditor and the trader on the same Tuesday.",
    image:
      "https://images.unsplash.com/photo-1762715461103-cf7f03090c93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjB0ZWNobm9sb2d5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NzMxMTE4MHww&ixlib=rb-4.1.0&q=85",
    capabilities: [
      "Trade finance and capital markets platforms",
      "Lending origination, decisioning, and servicing",
      "Payments, ledgers, and reconciliation engines",
      "Risk modelling, regulatory reporting, and audit trails",
      "Open Banking and PSD2-compliant APIs",
    ],
    regulators: "FCA · PRA · MAS · FINRA · ESMA",
    related_case: "northbank-trade-platform",
  },
  {
    id: "healthcare",
    name: "Healthcare & Life Sciences",
    headline: "Clinical-grade systems for care, claims, and continuity.",
    description:
      "EHR integration, care coordination, clinical trials, and provider-facing analytics. HIPAA and HITRUST aligned by default.",
    image:
      "https://images.pexels.com/photos/13156181/pexels-photo-13156181.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    capabilities: [
      "EHR integration and FHIR-aligned data fabrics",
      "Clinical trial management and ePRO platforms",
      "Care coordination and population-health analytics",
      "Patient-facing portals and telehealth",
      "Claims processing and provider workflows",
    ],
    regulators: "HIPAA · HITRUST · NHS DSP Toolkit · GDPR",
    related_case: "meridian-health-network",
  },
  {
    id: "ecommerce",
    name: "E-commerce & Retail",
    headline: "Composable commerce that actually composes.",
    description:
      "Federated catalog, identity-graph loyalty, headless storefronts, and the unglamorous order-management plumbing that keeps it standing.",
    image:
      "https://images.unsplash.com/photo-1753715613457-63127ec40824?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGNvZGUlMjBkYXJrJTIwc2NyZWVuJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc3MzExMTgwfDA&ixlib=rb-4.1.0&q=85",
    capabilities: [
      "Headless storefronts and composable commerce",
      "Order, inventory, and warehouse management",
      "Loyalty programs and identity-graph CRM",
      "Subscription and B2B commerce",
      "Marketplace operations and partner onboarding",
    ],
    regulators: "PCI DSS · GDPR · UK Consumer Rights Act",
    related_case: "tessera-commerce-replatform",
  },
  {
    id: "logistics",
    name: "Logistics & Supply Chain",
    headline: "Decision systems, not dashboards, for freight that moves.",
    description:
      "Pre-emptive recovery control towers, telematics-aware SLA engines, and warehouse software that respects the dock worker's time.",
    image:
      "https://images.pexels.com/photos/12902857/pexels-photo-12902857.jpeg",
    capabilities: [
      "Real-time visibility and pre-emptive control towers",
      "Transportation and warehouse management systems",
      "SLA, exception, and recovery engines",
      "Yard, dock, and last-mile orchestration",
      "Customs, trade, and document compliance automation",
    ],
    regulators: "HMRC · ICS2 · CTPAT · ELD",
    related_case: "polestar-logistics-control-tower",
  },
  {
    id: "education",
    name: "Education & EdTech",
    headline: "Outcomes-grade platforms for learners and the institutions that serve them.",
    description:
      "Adaptive assessment, learner record systems, instructor authoring tools, and analytics that measure learning—not minutes logged in.",
    image:
      "https://images.unsplash.com/photo-1665159693563-b8e51bc15545?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBhYnN0cmFjdCUyMGxvZ28lMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc3MzExMTgwfDA&ixlib=rb-4.1.0&q=85",
    capabilities: [
      "Adaptive assessment and item-response-theory platforms",
      "Learner record systems and credentialling",
      "LMS integration and instructor authoring tools",
      "Outcomes analytics and accreditation reporting",
      "Identity, SSO, and federated access for K-12 and HE",
    ],
    regulators: "FERPA · COPPA · UK DfE · GDPR-K",
    related_case: "lumen-edu-assessment",
  },
  {
    id: "saas",
    name: "SaaS & ISVs",
    headline: "Platform engineering for software companies who ship for a living.",
    description:
      "Multi-tenant architectures, billing, identity, observability, and the migration discipline that separates Series B from Series re-platform.",
    image:
      "https://images.pexels.com/photos/15876898/pexels-photo-15876898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    capabilities: [
      "Multi-tenant architecture and isolation patterns",
      "Billing, metering, and entitlement systems",
      "Identity, SSO, SCIM, and authorisation",
      "Observability, on-call, and incident discipline",
      "Migration programmes from monolith to platform",
    ],
    regulators: "SOC 2 · ISO 27001 · GDPR · CCPA",
    related_case: null,
  },
  {
    id: "government",
    name: "Government & Public Sector",
    headline: "Citizen services that respect the citizen's time.",
    description:
      "Accessible portals, identity verification, benefits administration, and audit trails compatible with state, federal, and FedRAMP frameworks.",
    image:
      "https://images.unsplash.com/photo-1665159693563-b8e51bc15545?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBhYnN0cmFjdCUyMGxvZ28lMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc3MzExMTgwfDA&ixlib=rb-4.1.0&q=85",
    capabilities: [
      "Citizen-facing portals and forms (WCAG 2.2 AA)",
      "Digital identity, verification, and SSO",
      "Benefits administration and case management",
      "Tamper-evident audit ledgers and FOIA workflows",
      "Hosting alignment with GovCloud, FedRAMP, and UK Crown",
    ],
    regulators: "GDS · FedRAMP · WCAG 2.2 AA · UK GDPR",
    related_case: "atlas-state-services-portal",
  },
];

export const PROCESS = [
  {
    n: "01",
    title: "Discovery & Diligence",
    body:
      "Three-week dive into the business, the systems, and the politics. We surface the constraints that the original RFP didn't.",
  },
  {
    n: "02",
    title: "Architecture & Roadmap",
    body:
      "A delivery-ready architecture, a sequenced roadmap, and a budget you can defend to a board. No vapourware diagrams.",
  },
  {
    n: "03",
    title: "Iterative Delivery",
    body:
      "Fortnightly increments. Production telemetry from day one. Every sprint produces something a stakeholder can actually click.",
  },
  {
    n: "04",
    title: "Hardening & Handover",
    body:
      "Documented runbooks, knowledge transfer engineered for staff turnover, and a 90-day post-launch standby. We leave on a Friday, not a sigh of relief.",
  },
];

export const STATS = [
  { number: "12", suffix: "+ yrs", label: "Building enterprise platforms" },
  { number: "180", suffix: "+", label: "Engineers, designers, strategists" },
  { number: "47", suffix: "", label: "Active enterprise engagements" },
  { number: "97", suffix: "%", label: "Client retention, last 36 months" },
];

export const TESTIMONIALS = [
  {
    quote:
      "Elite Solutions Hub didn't just build the platform. They rewrote the way our engineering organisation thinks about delivery.",
    author: "Helena R.",
    role: "Chief Technology Officer",
    company: "Northbank Capital",
  },
  {
    quote:
      "The thing they get right that almost no consultancy does is staying after the launch and earning the next contract on outcomes.",
    author: "Daniel V.",
    role: "VP of Digital",
    company: "Tessera Group",
  },
  {
    quote:
      "Surgical, opinionated, and genuinely good at the boring parts of software. They have become the team we benchmark others against.",
    author: "Dr. Anika P.",
    role: "Chief Information Officer",
    company: "Meridian Health Network",
  },
];

export const CLIENT_LOGOS = [
  "Northbank Capital",
  "Meridian Health",
  "Tessera Group",
  "Polestar Freight",
  "State of Atlas",
  "Lumen University",
  "Halcyon Bank",
  "Vantage SaaS",
  "Brightline Logistics",
  "Cordillera Health",
];

export const TECH_STACK = [
  "TypeScript", "Python", "Go", "Rust", "Java", "Kotlin", "Swift",
  "React", "Next.js", "Node.js", "FastAPI", "PostgreSQL", "MongoDB",
  "Kafka", "Snowflake", "Databricks", "AWS", "GCP", "Azure",
  "Terraform", "Kubernetes", "GitHub Actions", "Datadog", "Grafana",
];
